import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  PdfSize,
  ETextAlign,
  PdfUnit,
  PdfPen,
  PdfColor,
  PageGen,
  PdfPoint,
} from '../pdf';
import {
  Book,
  Chapter,
  Section,
  ContentElement,
  InlineElement,
  BookOptions,
  ParagraphElement,
  HeadingElement,
  ListElement,
  BlockquoteElement,
  CodeBlockElement,
  TableElement,
  ModuleElement,
  FloatElement,
} from './types';
import { LayoutParams, layoutParagraph, TextItem, LineBreakResult } from './layout';
import { NotesCollection, Note, renderFootnoteMarker, renderEndnoteMarker } from './notes';
import { moduleRegistry } from '../../modules';
import { layoutParagraphKnuth, inlinesToItems, breakLinesKnuth, renderLine } from './layout-knuth';
import { parseMarkdown, InlineNode } from '../markdown';
import { IImageLoader, IFileAccess } from '@educraft/base';

export interface RenderContext {
  page: PageGen;
  font: PdfFont;
  boldFont: PdfFont;
  italicFont: PdfFont;
  monoFont: PdfFont;
  currentY: number;
  lineHeight: number;
  pageHeight: number;
  pageWidth: number;
  margin: number;
  footnotes: Note[];
  endnotes: Note[];
}

export interface RenderResult {
  pages: number;
  footnotesPlaced: Map<number, Note[]>;
}

export interface MeasuredElement {
  type: string;
  height: number;
  width: number;
  data: any;
}

export interface MeasuredParagraph extends MeasuredElement {
  type: 'paragraph';
  lines: MeasuredLine[];
  totalHeight: number;
  align?: 'left' | 'center' | 'right' | 'justify';
}

export interface MeasuredLine {
  items: MeasuredInline[];
  width: number;
  height: number;
  ratio?: number; // Stretch/shrink ratio from Knuth algorithm for justification
}

export interface MeasuredInline {
  type:
    | 'text'
    | 'bold'
    | 'italic'
    | 'code'
    | 'link'
    | 'latex'
    | 'module'
    | 'footnote'
    | 'endnote'
    | 'citation';
  content: string;
  font: PdfFont;
  brush: PdfBrush;
  width: number;
  height: number;
  xOffset: number;
  children?: MeasuredInline[];
  href?: string;
  fontSize?: number;
  moduleName?: string;
  moduleContent?: any;
  noteContent?: string;
  noteId?: string;
  noteNumber?: number;
}

export class BookRenderer {
  private options: Required<Pick<BookOptions, Exclude<keyof BookOptions, 'imageLoader' | 'fileAccess'>>> & {
    imageLoader?: IImageLoader;
    fileAccess?: IFileAccess;
  };
  private language: string;
  private currentPageNumber = 1;

  constructor(options: BookOptions = {}) {
    this.options = {
      language: options.language || 'en',
      fontSize: options.fontSize || 12,
      lineHeight: options.lineHeight || 1.5,
      margin: options.margin || 20,
      columns: options.columns || 1,
      defaultAlignment: options.defaultAlignment || 'justify',
      title: options.title || '',
      imageLoader: options.imageLoader,
      fileAccess: options.fileAccess,
    };
    this.language = this.options.language;
  }

  render(pdf: PdfGen, book: Book, notes: NotesCollection): RenderResult {
    const pageWidth = PdfSize.A4.width - 2 * this.options.margin;
    const pageHeight = PdfSize.A4.height;

    const font = new PdfFont(PdfFont.FONT_NAME, this.options.fontSize);
    const boldFont = new PdfFont(PdfFont.BOLD_FONT_NAME, this.options.fontSize);
    const italicFont = new PdfFont(PdfFont.ITALIC_FONT_NAME, this.options.fontSize);
    const monoFont = new PdfFont(PdfFont.MONO_FONT_NAME, this.options.fontSize);

    const footnotesByPage = new Map<number, Note[]>();
    const allEndnotes: Note[] = [];

    for (const note of notes.getEndnotes()) {
      allEndnotes.push(note);
    }

    let currentPage = pdf.addPage();
    let pageNumber = 1;
    this.currentPageNumber = 1;
    let currentY = this.options.margin;

    const titleFont = new PdfFont(PdfFont.FONT_NAME, 24);
    const titleRect = new PdfRect(this.options.margin, currentY, pageWidth, 30);
    currentPage.drawString(
      book.title,
      titleFont,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.Center
    );
    currentY += 40;

    const headingFonts: PdfFont[] = [];
    for (let i = 1; i <= 6; i++) {
      const hFont = new PdfFont(PdfFont.FONT_NAME, 20 - i * 2);
      headingFonts.push(hFont);
    }

    // Dynamic footnote reserve: only reserve exactly the space the collected footnotes need.
    // Pages with no footnotes use ~(margin+5)pt; pages with N notes use (margin + N*13 + 5)pt.
    const getPageReserve = (pageNum: number): number =>
      this.calcFootnoteReserve((footnotesByPage.get(pageNum) || []).length);

    const minBodyLineHeight = 20; // ~12pt × 1.5 line height, ensures at least one body line fits after a heading

    for (const chapter of book.chapters) {
      const chapterHasContent = chapter.sections.some(s => s.content.length > 0 || s.title);
      if (currentY > pageHeight - getPageReserve(pageNumber) - 35 - (chapterHasContent ? minBodyLineHeight : 0)) {
        this.renderFootnotes(
          currentPage,
          footnotesByPage.get(pageNumber) || [],
          this.options.margin,
          pageWidth,
          font
        );
        footnotesByPage.set(pageNumber, []);
        currentPage = pdf.addPage();
        pageNumber++;
        this.currentPageNumber = pageNumber;
        currentY = this.options.margin;
      }

      const chapterTitleRect = new PdfRect(this.options.margin, currentY, pageWidth, 25);
      currentPage.drawString(
        chapter.title,
        headingFonts[0],
        PdfBrush.Black,
        null,
        chapterTitleRect,
        ETextAlign.CenterLeft
      );
      currentY += 35;

      for (const section of chapter.sections) {
        if (section.title) {
          if (currentY > pageHeight - getPageReserve(pageNumber) - 25 - (section.content.length > 0 ? minBodyLineHeight : 0)) {
            this.renderFootnotes(
              currentPage,
              footnotesByPage.get(pageNumber) || [],
              this.options.margin,
              pageWidth,
              font
            );
            footnotesByPage.set(pageNumber, []);
            currentPage = pdf.addPage();
            pageNumber++;
            this.currentPageNumber = pageNumber;
            currentY = this.options.margin;
          }

          const headingLevel = Math.min(section.level, 6) - 1;
          const sectionTitleRect = new PdfRect(this.options.margin, currentY, pageWidth, 20);
          currentPage.drawString(
            section.title,
            headingFonts[headingLevel],
            PdfBrush.Black,
            null,
            sectionTitleRect,
            ETextAlign.CenterLeft
          );
          currentY += 25;
        }

        // Track active floats for text wrapping
        let leftFloatWidth = 0;
        let rightFloatWidth = 0;
        let floatBottomY = currentY;

        for (let elIdx = 0; elIdx < section.content.length; elIdx++) {
          const element = section.content[elIdx];

          // Clear floats once we have scrolled past the float bottom
          if (currentY >= floatBottomY) {
            leftFloatWidth = 0;
            rightFloatWidth = 0;
          }

          // Calculate available width considering floats
          const availableWidth = pageWidth - leftFloatWidth - rightFloatWidth;

          // Handle floats specially - they don't split across pages
          if (element.type === 'float') {
            const measured = this.measureElement(
              element,
              availableWidth,
              font,
              boldFont,
              italicFont,
              monoFont,
              currentPage
            );

            const floatData = measured.data as {
              element: FloatElement;
              measuredContents: MeasuredElement[];
            };
            const floatEl = floatData.element;

            // Position float on the appropriate side
            const floatX =
              floatEl.align === 'right'
                ? this.options.margin + pageWidth - measured.width - rightFloatWidth
                : this.options.margin + leftFloatWidth;

            // Check if float fits, if not, start new page
            if (currentY + measured.height > pageHeight - getPageReserve(pageNumber)) {
              currentPage = pdf.addPage();
              pageNumber++;
              this.currentPageNumber = pageNumber;
              currentY = this.options.margin;
              footnotesByPage.set(pageNumber, []);
              leftFloatWidth = 0;
              rightFloatWidth = 0;
              floatBottomY = currentY;
            }

            // Render float at its position
            const result = this.renderMeasuredFloat(
              currentPage,
              measured,
              floatX,
              currentY,
              pageWidth,
              font,
              boldFont,
              italicFont,
              monoFont,
              notes
            );

            // Track float bounds for text wrapping
            if (floatEl.align === 'left') {
              leftFloatWidth += measured.width + 5;
              floatBottomY = Math.max(floatBottomY, currentY + measured.height);
            } else {
              rightFloatWidth += measured.width + 5;
              floatBottomY = Math.max(floatBottomY, currentY + measured.height);
            }

            currentY = result.newY;
          } else {
            // Regular element - render and handle page breaks internally
            const measured = this.measureElement(
              element,
              availableWidth,
              font,
              boldFont,
              italicFont,
              monoFont,
              currentPage
            );

            // Regular element - render with reduced width if floats active
            const elementX = this.options.margin + leftFloatWidth;

            const hasFollowingContent = elIdx + 1 < section.content.length;
            const minFollowingHeight =
              measured.type === 'heading' && hasFollowingContent
                ? this.measureMinFollowingHeight(
                    section.content[elIdx + 1],
                    availableWidth,
                    font,
                    boldFont,
                    italicFont,
                    monoFont,
                    currentPage
                  )
                : 0;

            const result = this.renderMeasuredElementWithBreak(
              currentPage,
              measured,
              elementX,
              currentY,
              availableWidth,
              pageWidth,
              pageHeight,
              font,
              boldFont,
              italicFont,
              monoFont,
              notes,
              pdf,
              footnotesByPage,
              (newPage: PageGen, newY: number, newPageNum: number) => {
                // Render and clear footnotes for the page we are leaving
                const oldNotes = footnotesByPage.get(pageNumber) || [];
                if (oldNotes.length > 0) {
                  this.renderFootnotes(currentPage, oldNotes, this.options.margin, pageWidth, font);
                  footnotesByPage.set(pageNumber, []);
                }
                currentPage = newPage;
                currentY = newY;
                pageNumber = newPageNum;
                this.currentPageNumber = newPageNum;
                // Floats don't carry over to a new page
                leftFloatWidth = 0;
                rightFloatWidth = 0;
                floatBottomY = newY;
              },
              minFollowingHeight
            );

            currentY = result.newY;

            // Clear floats once we have scrolled past the float bottom
            if (currentY >= floatBottomY) {
              leftFloatWidth = 0;
              rightFloatWidth = 0;
            }
          }

          // Check for page overflow after element rendering (leave space for footnotes)
          if (currentY > pageHeight - getPageReserve(pageNumber)) {
            this.renderFootnotes(
              currentPage,
              footnotesByPage.get(pageNumber) || [],
              this.options.margin,
              pageWidth,
              font
            );
            footnotesByPage.set(pageNumber, []);
            currentPage = pdf.addPage();
            pageNumber++;
            this.currentPageNumber = pageNumber;
            currentY = this.options.margin;
            leftFloatWidth = 0;
            rightFloatWidth = 0;
            floatBottomY = currentY;
          }
        }

        // After all elements in a section, ensure we've cleared any active float.
        // Without this, the next section/chapter would render on top of the float content.
        if (currentY < floatBottomY) {
          currentY = floatBottomY;
        }
      }
    }

    if (allEndnotes.length > 0) {
      if (currentY > pageHeight - getPageReserve(pageNumber) - 55) {
        currentPage = pdf.addPage();
        pageNumber++;
        this.currentPageNumber = pageNumber;
        currentY = this.options.margin;
      }

      const endnotesTitleRect = new PdfRect(this.options.margin, currentY, pageWidth, 25);
      currentPage.drawString(
        'Endnotes',
        headingFonts[0],
        PdfBrush.Black,
        null,
        endnotesTitleRect,
        ETextAlign.CenterLeft
      );
      currentY += 30;

      for (const endnote of allEndnotes) {
        const marker = renderEndnoteMarker(endnote.number || 0);
        const text = `${marker}. ${endnote.content}`;
        const textRect = new PdfRect(this.options.margin, currentY, pageWidth, 15);
        currentPage.drawString(text, font, PdfBrush.Black, null, textRect, ETextAlign.CenterLeft);
        currentY += 18;

        if (currentY > pageHeight - getPageReserve(pageNumber)) {
          currentPage = pdf.addPage();
          pageNumber++;
          this.currentPageNumber = pageNumber;
          currentY = this.options.margin;
        }
      }
    }

    this.renderFootnotes(
      currentPage,
      footnotesByPage.get(pageNumber) || [],
      this.options.margin,
      pageWidth,
      font
    );

    return {
      pages: pageNumber,
      footnotesPlaced: footnotesByPage,
    };
  }

  private measureMinFollowingHeight(
    element: ContentElement,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    page: PageGen
  ): number {
    if (element.type === 'list') {
      const listEl = element as ListElement;
      if (listEl.items.length > 0) {
        const firstItem = listEl.items[0];
        const itemWidth = lineWidth - 20; // 20 = list indent
        const measuredPara = this.measureParagraph(
          { type: 'paragraph', inlines: firstItem.inlines },
          itemWidth > 0 ? itemWidth : lineWidth,
          font, boldFont, italicFont, monoFont, page
        );
        return measuredPara.totalHeight + 2.5;
      }
      return 20;
    }
    if (element.type === 'paragraph') {
      return font.size * this.options.lineHeight + 2;
    }
    const measured = this.measureElement(element, lineWidth, font, boldFont, italicFont, monoFont, page);
    return Math.min(measured.height, 60);
  }

  private measureElement(
    element: ContentElement,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    page: PageGen
  ): MeasuredElement {
    switch (element.type) {
      case 'paragraph':
        return this.measureParagraph(
          element,
          lineWidth,
          font,
          boldFont,
          italicFont,
          monoFont,
          page
        );
      case 'heading':
        return this.measureHeading(element, lineWidth, font);
      case 'list':
        return this.measureList(element, lineWidth, font, boldFont, italicFont, monoFont, page);
      case 'blockquote':
        return this.measureBlockquote(element, lineWidth, font, italicFont);
      case 'codeBlock':
        return this.measureCodeBlock(element, lineWidth, monoFont);
      case 'table':
        return this.measureTable(element, lineWidth, font, boldFont, page);
      case 'module':
        return this.measureModule(element, lineWidth, font, page);
      case 'float':
        return this.measureFloat(element, lineWidth, font, boldFont, italicFont, monoFont, page);
      case 'hr':
        return this.measureHr(lineWidth);
      default:
        return { type: 'unknown', height: 0, width: 0, data: null };
    }
  }

  private measureParagraph(
    element: ParagraphElement,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    page: PageGen
  ): MeasuredParagraph {
    const layoutParams: LayoutParams = {
      font,
      page,
      lineWidth,
      tolerance: 2,
    };

    const result = layoutParagraphKnuth(
      element.inlines,
      layoutParams,
      boldFont,
      italicFont,
      monoFont,
      this.language
    );
    const lines: MeasuredLine[] = [];

    let prevBreak = 0;
    const lineHeight = this.options.fontSize * this.options.lineHeight;

    for (const breakPoint of result.breaks) {
      const lineItems: MeasuredInline[] = [];
      let lineWidth = 0;

      for (let i = prevBreak; i < breakPoint.position && i < result.items.length; i++) {
        const item = result.items[i];
        if (item.type === 'box' && item.font && item.brush) {
          if (item.isLatex && item.content) {
            const measuredInline: MeasuredInline = {
              type: 'latex',
              content: item.content,
              font: item.font,
              brush: item.brush,
              width: item.width,
              height: lineHeight,
              xOffset: lineWidth,
              fontSize: item.fontSize,
            };
            lineItems.push(measuredInline);
          } else if (item.isModule && item.moduleName && item.moduleContent) {
            const measuredInline: MeasuredInline = {
              type: 'module',
              content: '',
              font: item.font,
              brush: item.brush,
              width: item.width,
              height: lineHeight,
              xOffset: lineWidth,
              moduleName: item.moduleName,
              moduleContent: item.moduleContent,
            };
            lineItems.push(measuredInline);
          } else if (item.isNote && item.noteType) {
            const measuredInline: MeasuredInline = {
              type: item.noteType,
              content: item.content || '',
              font: item.font,
              brush: item.brush,
              width: item.width,
              height: lineHeight,
              xOffset: lineWidth,
              noteContent: item.noteContent,
              noteId: item.noteId,
              noteNumber: item.noteNumber,
            };
            lineItems.push(measuredInline);
          } else if (item.content) {
            const measuredInline: MeasuredInline = {
              type: 'text',
              content: item.content,
              font: item.font,
              brush: item.brush,
              width: item.width,
              height: lineHeight,
              xOffset: lineWidth,
            };
            lineItems.push(measuredInline);
          }
          lineWidth += item.width;
        } else if (item.type === 'glue') {
          // Create a space item
          const measuredInline: MeasuredInline = {
            type: 'text',
            content: ' ',
            font, // Use base font for spaces
            brush: PdfBrush.Black,
            width: item.width,
            height: lineHeight,
            xOffset: lineWidth,
          };
          lineItems.push(measuredInline);
          lineWidth += item.width;
        }
      }

      // Trim trailing space (glue at break point is discarded, must not affect line width)
      while (lineItems.length > 0 && lineItems[lineItems.length - 1].content === ' ') {
        lineWidth -= lineItems[lineItems.length - 1].width;
        lineItems.pop();
      }

      // If break is at a hyphenation penalty, append the '-' to the line
      const breakItem = breakPoint.position > 0 ? result.items[breakPoint.position - 1] : null;
      if (breakItem?.type === 'penalty' && breakItem.content === '-') {
        const hyphenFont = breakItem.font || font;
        const hyphenBrush = breakItem.brush || PdfBrush.Black;
        lineItems.push({
          type: 'text',
          content: '-',
          font: hyphenFont,
          brush: hyphenBrush,
          width: breakItem.width || 0,
          height: lineHeight,
          xOffset: lineWidth,
        });
        lineWidth += breakItem.width || 0;
      }

      if (lineItems.length > 0) {
        lines.push({
          items: lineItems,
          width: lineWidth,
          height: lineHeight,
          ratio: breakPoint.ratio, // Store the ratio from Knuth algorithm
        });
      }

      prevBreak = breakPoint.position;
    }

    const totalHeight = lines.reduce((sum, line) => sum + line.height, 0) + (lines.length - 1) * 2;

    return {
      type: 'paragraph',
      lines,
      totalHeight,
      height: totalHeight,
      width: lineWidth,
      data: element,
      align: element.align || this.options.defaultAlignment,
    };
  }

  private measureHeading(
    element: HeadingElement,
    lineWidth: number,
    font: PdfFont
  ): MeasuredElement {
    const headingFont = new PdfFont(PdfFont.FONT_NAME, 20 - element.level * 2);
    const height = 25;

    return {
      type: 'heading',
      height,
      width: lineWidth,
      data: element,
    };
  }

  private measureList(
    element: ListElement,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    page: PageGen,
    baseIndent = 0
  ): MeasuredElement {
    let totalHeight = 0;
    const indent = 20;
    const currentIndent = baseIndent + indent;
    const itemWidth = lineWidth - currentIndent;

    for (const item of element.items) {
      const measuredPara = this.measureParagraph(
        { type: 'paragraph', inlines: item.inlines },
        itemWidth > 0 ? itemWidth : lineWidth,
        font,
        boldFont,
        italicFont,
        monoFont,
        page
      );
      totalHeight += measuredPara.totalHeight;

      if (item.children && item.children.length > 0) {
        for (const childList of item.children) {
          const measuredChild = this.measureList(
            childList,
            lineWidth,
            font,
            boldFont,
            italicFont,
            monoFont,
            page,
            currentIndent
          );
          totalHeight += measuredChild.height;
        }
      }

      totalHeight += 2.5;
    }

    return {
      type: 'list',
      height: totalHeight,
      width: lineWidth,
      data: element,
    };
  }

  private measureBlockquote(
    element: BlockquoteElement,
    lineWidth: number,
    font: PdfFont,
    italicFont: PdfFont
  ): MeasuredElement {
    const height = 20;

    return {
      type: 'blockquote',
      height,
      width: lineWidth,
      data: element,
    };
  }

  private measureCodeBlock(
    element: CodeBlockElement,
    lineWidth: number,
    monoFont: PdfFont
  ): MeasuredElement {
    const lines = element.content.split('\n');
    const lineHeight = monoFont.size * 1.5;
    const padding = 10;
    const totalHeight = lines.length * lineHeight + padding * 2;

    return {
      type: 'codeBlock',
      height: totalHeight,
      width: lineWidth,
      data: element,
    };
  }

  private measureTable(
    element: TableElement,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    page: PageGen
  ): MeasuredElement {
    // Calculate column widths based on content (including inline LaTeX/modules)
    const colCount = element.headers.length;
    const colWidths = this.calculateColumnWidths(element, lineWidth, font, boldFont, page);

    // Calculate heights for each row based on content
    const headerHeight = 20;
    const rowHeights = element.rows.map((_, rowIdx) => {
      let maxHeight = 18;
      for (let colIdx = 0; colIdx < colCount; colIdx++) {
        const cellInlines = element.cells?.[rowIdx]?.[colIdx];
        if (cellInlines && cellInlines.length > 0) {
          const cellHeight = this.measureInlineContentHeight(
            cellInlines,
            colWidths[colIdx],
            font,
            page
          );
          maxHeight = Math.max(maxHeight, cellHeight);
        }
      }
      return maxHeight;
    });

    const separatorHeight = 10;
    const padding = 5;
    const totalHeight =
      headerHeight + separatorHeight + rowHeights.reduce((a, b) => a + b, 0) + padding * 2;

    return {
      type: 'table',
      height: totalHeight,
      width: lineWidth,
      data: { ...element, colWidths, rowHeights },
    };
  }

  private measureInlineContentHeight(
    inlines: InlineElement[],
    maxWidth: number,
    font: PdfFont,
    page: PageGen
  ): number {
    // For now, return a simple height - in the future could do proper multi-line measurement
    const lineHeight = font.size * 1.5;
    return Math.max(18, lineHeight);
  }

  private calculateColumnWidths(
    element: TableElement,
    maxWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    page: PageGen
  ): number[] {
    const colCount = element.headers.length;
    const minColWidth = 40; // Minimum column width in points
    const maxColWidth = maxWidth / 2; // Maximum column width

    // Calculate required width for each column
    const requiredWidths: number[] = [];

    for (let i = 0; i < colCount; i++) {
      // Measure header (text or inline)
      let headerWidth = 0;
      if (element.headerCells?.[i]) {
        headerWidth = this.measureInlineContentWidth(element.headerCells[i], boldFont, page);
      } else {
        headerWidth = this.measureTextWidth(element.headers[i], boldFont, page);
      }

      // Measure all cell contents in this column
      let maxCellWidth = headerWidth;
      for (let rowIdx = 0; rowIdx < element.rows.length; rowIdx++) {
        const cellInlines = element.cells?.[rowIdx]?.[i];
        if (cellInlines && cellInlines.length > 0) {
          const cellWidth = this.measureInlineContentWidth(cellInlines, font, page);
          maxCellWidth = Math.max(maxCellWidth, cellWidth);
        } else if (i < element.rows[rowIdx].length) {
          const cellWidth = this.measureTextWidth(element.rows[rowIdx][i] || '', font, page);
          maxCellWidth = Math.max(maxCellWidth, cellWidth);
        }
      }

      // Add padding
      const paddedWidth = Math.min(Math.max(maxCellWidth + 20, minColWidth), maxColWidth);
      requiredWidths.push(paddedWidth);
    }

    // Distribute available width proportionally
    const totalRequired = requiredWidths.reduce((sum, w) => sum + w, 0);

    if (totalRequired <= maxWidth) {
      // We have enough space, use required widths
      return requiredWidths;
    } else {
      // Need to scale down proportionally
      const scale = maxWidth / totalRequired;
      return requiredWidths.map((w) => Math.max(minColWidth, w * scale));
    }
  }

  private measureInlineContentWidth(
    inlines: InlineElement[],
    font: PdfFont,
    page: PageGen
  ): number {
    let totalWidth = 0;
    for (const inline of inlines) {
      switch (inline.type) {
        case 'text':
          totalWidth += this.measureTextWidth(inline.content, font, page);
          break;
        case 'bold':
        case 'italic':
          // Measure children
          for (const child of inline.children || []) {
            if (child.type === 'text') {
              totalWidth += this.measureTextWidth(child.content, font, page);
            }
          }
          break;
        case 'code':
          totalWidth += this.measureTextWidth(inline.content, font, page);
          break;
        case 'latex':
          totalWidth += page.measureLatex(inline.content, font.size).width;
          break;
        case 'module': {
          const mod = moduleRegistry.get(inline.moduleName);
          if (mod) {
            const content = mod.parse(inline.content, inline.attrs);
            const size = mod.measure(content, { font, page });
            totalWidth += size.width;
          } else {
            totalWidth += this.measureTextWidth(`<${inline.moduleName}>`, font, page);
          }
          break;
        }
      }
      // Add space after each inline element (except last)
      totalWidth += this.measureTextWidth(' ', font, page);
    }
    return totalWidth;
  }

  private measureTextWidth(text: string, font: PdfFont, page: PageGen): number {
    try {
      // Use actual PDF font metrics if available
      const pdfFont = page.pdf.getFont(font.font);
      return pdfFont.widthOfTextAtSize(text, font.size);
    } catch (error) {
      // Fallback to estimation
      const avgCharWidth = font.size * 0.6;
      return text.length * avgCharWidth;
    }
  }

  private measureModule(
    element: ModuleElement,
    lineWidth: number,
    font: PdfFont,
    page: PageGen
  ): MeasuredElement {
    const module = moduleRegistry.get(element.moduleName);

    if (module) {
      try {
        const content = module.parse(element.content, element.attrs);
        const size = module.measure(content, { font, page });
        return {
          type: 'module',
          height: size.height + 10,
          width: Math.min(size.width, lineWidth),
          data: { element, content, module },
        };
      } catch (e) {
        return {
          type: 'module-error',
          height: 20,
          width: lineWidth,
          data: { error: (e as Error).message },
        };
      }
    }

    return {
      type: 'module-not-found',
      height: 20,
      width: lineWidth,
      data: { moduleName: element.moduleName },
    };
  }

  private measureFloat(
    element: FloatElement,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    page: PageGen
  ): MeasuredElement {
    // Calculate width as percentage of line width
    const floatWidth = element.width ? (lineWidth * element.width) / 100 : lineWidth * 0.4; // Default 40%

    // Measure each content block inside the float
    const measuredContents = element.content.map((c) =>
      this.measureElement(c, floatWidth, font, boldFont, italicFont, monoFont, page)
    );

    // Sum heights; paragraphs add +7 for last-line gap + trailing spacing
    let totalHeight = 0;
    for (const m of measuredContents) {
      totalHeight += m.height;
      if (m.type === 'paragraph') totalHeight += 7;
    }

    return {
      type: 'float',
      height: totalHeight,
      width: floatWidth,
      data: { element, measuredContents },
    };
  }

  private measureHr(lineWidth: number): MeasuredElement {
    return {
      type: 'hr',
      height: 15,
      width: lineWidth,
      data: null,
    };
  }

  private renderMeasuredElement(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    notes: NotesCollection
  ): { newY: number; footnote?: Note } {
    switch (measured.type) {
      case 'paragraph':
        return this.renderMeasuredParagraph(
          page,
          measured as MeasuredParagraph,
          x,
          currentY,
          lineWidth,
          font,
          boldFont,
          italicFont,
          monoFont,
          notes
        );
      case 'heading':
        return this.renderMeasuredHeading(page, measured, x, currentY, lineWidth, font);
      case 'list':
        return this.renderMeasuredList(
          page,
          measured,
          x,
          currentY,
          lineWidth,
          font,
          boldFont,
          italicFont,
          monoFont,
          notes
        );
      case 'blockquote':
        return this.renderMeasuredBlockquote(
          page,
          measured,
          x,
          currentY,
          lineWidth,
          font,
          italicFont
        );
      case 'codeBlock':
        return this.renderMeasuredCodeBlock(page, measured, x, currentY, lineWidth, monoFont);
      case 'table':
        return this.renderMeasuredTable(page, measured, x, currentY, lineWidth, font, boldFont);
      case 'module':
        return this.renderMeasuredModule(page, measured, x, currentY, lineWidth, font);
      case 'float':
        return this.renderMeasuredFloat(
          page,
          measured,
          x,
          currentY,
          lineWidth,
          font,
          boldFont,
          italicFont,
          monoFont,
          notes
        );
      case 'hr':
        return this.renderMeasuredHr(page, measured, x, currentY, lineWidth);
      default:
        return { newY: currentY };
    }
  }

  private renderMeasuredElementWithBreak(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    pageWidth: number,
    pageHeight: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    notes: NotesCollection,
    pdf: PdfGen,
    footnotesByPage: Map<number, Note[]>,
    onPageBreak: (newPage: PageGen, newY: number, newPageNum: number) => void,
    minFollowingHeight = 0
  ): { newY: number; footnote?: Note } {
    const {margin} = this.options;
    const minLinesOnPage = 3; // Orphan/widow protection: min lines to keep together

    switch (measured.type) {
      case 'paragraph':
        return this.renderMeasuredParagraphWithBreak(
          page,
          measured as MeasuredParagraph,
          x,
          currentY,
          lineWidth,
          pageHeight,
          margin,
          minLinesOnPage,
          font,
          boldFont,
          italicFont,
          monoFont,
          notes,
          pdf,
          footnotesByPage,
          onPageBreak
        );
      case 'list':
        return this.renderMeasuredListWithBreak(
          page,
          measured,
          x,
          currentY,
          lineWidth,
          pageHeight,
          margin,
          font,
          boldFont,
          italicFont,
          monoFont,
          notes,
          pdf,
          footnotesByPage,
          onPageBreak
        );
      case 'table':
        return this.renderMeasuredTableWithBreak(
          page,
          measured,
          x,
          currentY,
          lineWidth,
          pageHeight,
          margin,
          font,
          boldFont,
          pdf,
          footnotesByPage,
          onPageBreak
        );
      default: {
        // For non-splittable elements (code blocks, blockquotes, headings, hr, modules),
        // check if the element fits above the footnote zone; if not, start a new page first.
        let renderPage = page;
        let renderY = currentY;
        if (
          renderY + measured.height + minFollowingHeight >
          pageHeight -
            this.calcFootnoteReserve((footnotesByPage.get(this.currentPageNumber) || []).length)
        ) {
          renderPage = pdf.addPage();
          this.currentPageNumber++;
          renderY = margin;
          onPageBreak(renderPage, renderY, this.currentPageNumber);
        }
        return this.renderMeasuredElement(
          renderPage,
          measured,
          x,
          renderY,
          lineWidth,
          font,
          boldFont,
          italicFont,
          monoFont,
          notes
        );
      }
    }
  }

  private renderMeasuredParagraphWithBreak(
    page: PageGen,
    measured: MeasuredParagraph,
    x: number,
    currentY: number,
    lineWidth: number,
    pageHeight: number,
    margin: number,
    minLinesOnPage: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    notes: NotesCollection,
    pdf: PdfGen,
    footnotesByPage: Map<number, Note[]>,
    onPageBreak: (newPage: PageGen, newY: number, newPageNum: number) => void
  ): { newY: number } {
    let y = currentY;
    let footnotes: Note[] = [];
    const lineHeight = measured.lines.length > 0 ? measured.lines[0].height + 2 : 15;
    // Pre-scan this paragraph for footnotes so we can include them in the reserve.
    // This ensures the last line's footnote doesn't push into the footnote zone.
    const paragraphNoteCount = measured.lines.reduce(
      (sum, ln) =>
        sum + ln.items.filter((it) => it.type === 'footnote' || it.type === 'citation').length,
      0
    );
    const getBottomMargin = (): number => {
      const pageNoteCount = (footnotesByPage.get(this.currentPageNumber) || []).length;
      return this.calcFootnoteReserve(pageNoteCount + paragraphNoteCount);
    };

    for (let lineIdx = 0; lineIdx < measured.lines.length; lineIdx++) {
      const line = measured.lines[lineIdx];
      const isLastLine = lineIdx === measured.lines.length - 1;

      // Check if we need a new page (Y increases downward from top)
      const availableHeight = pageHeight - y - getBottomMargin();

      if (availableHeight < line.height + 10) {
        // Line doesn't fit — always start a new page (covers last line and all other cases)
        if (footnotes.length > 0) {
          const existing = footnotesByPage.get(this.currentPageNumber) || [];
          footnotesByPage.set(this.currentPageNumber, [...existing, ...footnotes]);
          footnotes = [];
        }
        page = pdf.addPage();
        this.currentPageNumber++;
        y = margin;
        onPageBreak(page, y, this.currentPageNumber);
      }

      // Render this line
      const result = this.renderMeasuredLine(
        page,
        measured,
        line,
        lineIdx,
        x,
        y,
        lineWidth,
        font,
        boldFont,
        italicFont,
        monoFont
      );

      for (const note of result.footnotes) {
        footnotes.push(note);
      }

      y += line.height + 2;
    }

    if (footnotes.length > 0) {
      const existing = footnotesByPage.get(this.currentPageNumber) || [];
      footnotesByPage.set(this.currentPageNumber, [...existing, ...footnotes]);
    }

    return { newY: y + 5 };
  }

  private renderMeasuredListWithBreak(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    pageHeight: number,
    margin: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    notes: NotesCollection,
    pdf: PdfGen,
    footnotesByPage: Map<number, Note[]>,
    onPageBreak: (newPage: PageGen, newY: number, newPageNum: number) => void,
    baseIndent = 0
  ): { newY: number } {
    const element = measured.data as ListElement;
    let y = currentY;
    let currentPage = page;
    const indent = 20;
    const currentIndent = baseIndent + indent;
    const lineHeight = this.options.fontSize * this.options.lineHeight;

    for (let i = 0; i < element.items.length; i++) {
      const item = element.items[i];
      const prefix = element.ordered ? `${i + 1}. ` : '• ';
      const itemWidth = lineWidth - currentIndent > 0 ? lineWidth - currentIndent : lineWidth;

      const measuredPara = this.measureParagraph(
        { type: 'paragraph', inlines: item.inlines },
        itemWidth,
        font,
        boldFont,
        italicFont,
        monoFont,
        currentPage
      );

      // Pre-scan item for footnotes to include in dynamic reserve
      const itemNoteCount = measuredPara.lines.reduce(
        (sum, ln) =>
          sum + ln.items.filter((it) => it.type === 'footnote' || it.type === 'citation').length,
        0
      );
      const pageNoteCount = (footnotesByPage.get(this.currentPageNumber) || []).length;
      const bottomMargin = this.calcFootnoteReserve(pageNoteCount + itemNoteCount);

      // Check page overflow before rendering this item
      if (y + measuredPara.totalHeight > pageHeight - bottomMargin && y > margin + lineHeight) {
        currentPage = pdf.addPage();
        this.currentPageNumber++;
        y = margin;
        footnotesByPage.set(this.currentPageNumber, []);
        onPageBreak(currentPage, y, this.currentPageNumber);
      }

      // Draw prefix (full indent width so multi-digit numbers never clip)
      const prefixRect = new PdfRect(x + baseIndent, y, indent, lineHeight);
      currentPage.drawString(prefix, font, PdfBrush.Black, null, prefixRect, ETextAlign.CenterLeft);

      // Draw item content
      const result = this.renderMeasuredParagraph(
        currentPage,
        measuredPara,
        x + currentIndent,
        y,
        itemWidth,
        font,
        boldFont,
        italicFont,
        monoFont,
        notes
      );

      // Remove the +7 added by renderMeasuredParagraph so spacing comes only from +2.5 below
      y = result.newY - 7;

      // Draw nested children
      if (item.children && item.children.length > 0) {
        for (const childList of item.children) {
          const measuredChild = this.measureList(
            childList,
            lineWidth,
            font,
            boldFont,
            italicFont,
            monoFont,
            currentPage,
            currentIndent
          );
          const childResult = this.renderMeasuredListWithBreak(
            currentPage,
            measuredChild,
            x,
            y,
            lineWidth,
            pageHeight,
            margin,
            font,
            boldFont,
            italicFont,
            monoFont,
            notes,
            pdf,
            footnotesByPage,
            (newPage, newY, newPageNum) => {
              currentPage = newPage;
              y = newY;
              onPageBreak(newPage, newY, newPageNum);
            },
            currentIndent
          );
          y = childResult.newY;
        }
      }

      y += 2.5;
    }

    return { newY: y };
  }

  private renderMeasuredLine(
    page: PageGen,
    measured: MeasuredParagraph,
    line: MeasuredLine,
    lineIdx: number,
    x: number,
    y: number,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont
  ): { footnotes: Note[] } {
    const collectedNotes: Note[] = [];
    const align = measured.align || 'justify';
    const isLastLine = lineIdx === measured.lines.length - 1;

    // Calculate positions for each item based on alignment
    const positions: { item: MeasuredInline; x: number }[] = [];

    // Count spaces in the line
    let spaceCount = 0;
    for (const item of line.items) {
      if (item.content === ' ') spaceCount++;
    }

    // Compute spaceIncrement:
    // - justify (non-last): always distribute delta (stretch or shrink)
    // - all other modes: only compress if line overflows (line.width > lineWidth)
    let spaceIncrement = 0;
    const isJustifyNonLast = align === 'justify' && !isLastLine;
    if (isJustifyNonLast) {
      if (spaceCount > 0) {
        spaceIncrement = (lineWidth - line.width) / spaceCount;
      }
    } else if (line.width > lineWidth && spaceCount > 0) {
      spaceIncrement = (lineWidth - line.width) / spaceCount;
    }

    const adjustedWidth = line.width + spaceIncrement * spaceCount;

    // Compute startX based on alignment
    let startX: number;
    if (align === 'center') {
      startX = x + (lineWidth - adjustedWidth) / 2;
    } else if (align === 'right') {
      startX = x + lineWidth - adjustedWidth;
    } else {
      // left, justify (last line), justify (non-last)
      startX = x;
    }

    // Place items
    let currentX = startX;
    for (const item of line.items) {
      positions.push({ item, x: currentX });
      currentX += item.width;
      if (item.content === ' ') {
        currentX += spaceIncrement;
      }
    }

    // Render items at calculated positions
    for (const { item, x: itemX } of positions) {
      const rect = new PdfRect(itemX, y, item.width, line.height);

      if (item.type === 'footnote' || item.type === 'endnote' || item.type === 'citation') {
        // Render as superscript: smaller font, shifted slightly up
        const superRect = new PdfRect(
          itemX,
          y + line.height * 0.05,
          item.width,
          line.height * 0.65
        );
        page.drawString(item.content, item.font, item.brush, null, superRect, ETextAlign.TopLeft);
        if (item.noteContent !== undefined && item.noteNumber !== undefined) {
          collectedNotes.push({
            id: `${item.type}-${item.noteNumber}`,
            type: item.type,
            content: item.noteContent,
            number: item.noteNumber,
            position: { start: 0, end: 0 },
          });
        }
      } else if (item.type === 'latex' && item.content) {
        const fontSize = item.fontSize || item.font.size;
        page.drawLatex(item.content, rect, ETextAlign.CenterLeft, fontSize, item.brush, false);
      } else if (item.type === 'module' && item.moduleName && item.moduleContent) {
        const mod = moduleRegistry.get(item.moduleName);
        if (mod) {
          mod.draw(item.moduleContent, rect, ETextAlign.CenterLeft, {
            font: item.font,
            brush: item.brush,
            page,
          });
        }
      } else if (item.content) {
        page.drawString(item.content, item.font, item.brush, null, rect, ETextAlign.CenterLeft);
      }

      if (item.type === 'link' && item.href) {
        const underlinePen = new PdfPen(PdfColor.fromRgb(0, 0, 255), 0.5);
        const start = new PdfPoint(itemX, y + line.height - 2);
        const end = new PdfPoint(itemX + item.width, y + line.height - 2);
        page.drawLine(underlinePen, start, end);
      }
    }

    return { footnotes: collectedNotes };
  }

  private renderMeasuredTableWithBreak(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    pageHeight: number,
    margin: number,
    font: PdfFont,
    boldFont: PdfFont,
    pdf: PdfGen,
    footnotesByPage: Map<number, Note[]>,
    onPageBreak: (newPage: PageGen, newY: number, newPageNum: number) => void
  ): { newY: number } {
    const data = measured.data as TableElement & { colWidths?: number[]; rowHeights?: number[] };
    const colWidths = data.colWidths || [];
    const rowHeights = data.rowHeights || [];
    const colCount = data.headers.length;

    const headerHeight = 20;
    const separatorHeight = 2;
    const cellPadding = 3;

    let y = currentY;

    // Render headers
    const tableWidth = colWidths.reduce((a, b) => a + b, 0);
    const headerBgRect = new PdfRect(x, y, tableWidth, headerHeight);
    page.drawRectangle(null, new PdfBrush(PdfColor.fromRgb(240, 240, 240)), headerBgRect);

    let currentX = x;
    for (let i = 0; i < colCount; i++) {
      const cellRect = new PdfRect(currentX, y, colWidths[i], headerHeight);
      const headerInlines = data.headerCells?.[i];
      if (headerInlines && headerInlines.length > 0) {
        this.renderInlineContentInCell(page, headerInlines, cellRect, boldFont, ETextAlign.Center);
      } else {
        page.drawString(
          data.headers[i],
          boldFont,
          PdfBrush.Black,
          null,
          cellRect,
          ETextAlign.Center
        );
      }
      const borderPen = new PdfPen(PdfColor.fromRgb(100, 100, 100), 0.5);
      page.drawRectangle(borderPen, null, cellRect);
      currentX += colWidths[i];
    }
    y += headerHeight;

    const separatorRect = new PdfRect(x, y, tableWidth, separatorHeight);
    page.drawRectangle(null, new PdfBrush(PdfColor.fromRgb(100, 100, 100)), separatorRect);
    y += separatorHeight;

    // Render rows with page break handling
    const pageStartY = y;
    for (let rowIdx = 0; rowIdx < data.rows.length; rowIdx++) {
      const rowHeight = rowHeights[rowIdx] || 18;

      // Check if row fits on current page (leave space for footnotes)
      if (
        y + rowHeight >
        pageHeight -
          this.calcFootnoteReserve((footnotesByPage.get(this.currentPageNumber) || []).length)
      ) {
        // New page needed
        page = pdf.addPage();
        this.currentPageNumber++;
        y = margin;
        onPageBreak(page, y, this.currentPageNumber);

        // Repeat header on new page
        const repeatHeaderRect = new PdfRect(x, y, tableWidth, headerHeight);
        page.drawRectangle(null, new PdfBrush(PdfColor.fromRgb(240, 240, 240)), repeatHeaderRect);

        currentX = x;
        for (let i = 0; i < colCount; i++) {
          const cellRect = new PdfRect(currentX, y, colWidths[i], headerHeight);
          const headerInlines = data.headerCells?.[i];
          if (headerInlines && headerInlines.length > 0) {
            this.renderInlineContentInCell(
              page,
              headerInlines,
              cellRect,
              boldFont,
              ETextAlign.Center
            );
          } else {
            page.drawString(
              data.headers[i],
              boldFont,
              PdfBrush.Black,
              null,
              cellRect,
              ETextAlign.Center
            );
          }
          const borderPen = new PdfPen(PdfColor.fromRgb(100, 100, 100), 0.5);
          page.drawRectangle(borderPen, null, cellRect);
          currentX += colWidths[i];
        }
        y += headerHeight;

        const repeatSepRect = new PdfRect(x, y, tableWidth, separatorHeight);
        page.drawRectangle(null, new PdfBrush(PdfColor.fromRgb(100, 100, 100)), repeatSepRect);
        y += separatorHeight;
      }

      // Render row
      currentX = x;
      for (let i = 0; i < colCount; i++) {
        const cellRect = new PdfRect(currentX, y, colWidths[i], rowHeight);
        const cellInlines = data.cells?.[rowIdx]?.[i];
        if (cellInlines && cellInlines.length > 0) {
          this.renderInlineContentInCell(page, cellInlines, cellRect, font, ETextAlign.Center);
        } else {
          page.drawString(
            data.rows[rowIdx][i] || '',
            font,
            PdfBrush.Black,
            null,
            cellRect,
            ETextAlign.Center
          );
        }
        const borderPen = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
        page.drawRectangle(borderPen, null, cellRect);
        currentX += colWidths[i];
      }
      y += rowHeight;
    }

    return { newY: y + 10 };
  }

  private renderMeasuredParagraph(
    page: PageGen,
    measured: MeasuredParagraph,
    x: number,
    currentY: number,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    notes: NotesCollection
  ): { newY: number } {
    let y = currentY;
    for (let lineIdx = 0; lineIdx < measured.lines.length; lineIdx++) {
      const line = measured.lines[lineIdx];
      this.renderMeasuredLine(
        page,
        measured,
        line,
        lineIdx,
        x,
        y,
        lineWidth,
        font,
        boldFont,
        italicFont,
        monoFont
      );
      y += line.height + 2;
    }
    return { newY: y + 5 };
  }

  private renderMeasuredHeading(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    font: PdfFont
  ): { newY: number; footnote?: Note } {
    const element = measured.data as HeadingElement;
    const headingFont = new PdfFont(PdfFont.FONT_NAME, 20 - element.level * 2);
    const rect = new PdfRect(x, currentY, lineWidth, 20);
    page.drawString(element.text, headingFont, PdfBrush.Black, null, rect, ETextAlign.CenterLeft);
    return { newY: currentY + 25 };
  }

  private renderMeasuredList(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    notes: NotesCollection,
    baseIndent = 0
  ): { newY: number; footnote?: Note } {
    const element = measured.data as ListElement;
    let y = currentY;
    const indent = 20;
    const currentIndent = baseIndent + indent;
    const lineHeight = this.options.fontSize * this.options.lineHeight;

    for (let i = 0; i < element.items.length; i++) {
      const item = element.items[i];
      const prefix = element.ordered ? `${i + 1}. ` : '• ';

      // Use the full indent width for the prefix rect so multi-digit numbers don't clip
      const prefixRect = new PdfRect(x + baseIndent, y, indent, lineHeight);
      page.drawString(prefix, font, PdfBrush.Black, null, prefixRect, ETextAlign.CenterLeft);

      const itemWidth = lineWidth - currentIndent;
      const measuredPara = this.measureParagraph(
        { type: 'paragraph', inlines: item.inlines },
        itemWidth > 0 ? itemWidth : lineWidth,
        font,
        boldFont,
        italicFont,
        monoFont,
        page
      );

      const result = this.renderMeasuredParagraph(
        page,
        measuredPara,
        x + currentIndent,
        y,
        itemWidth > 0 ? itemWidth : lineWidth,
        font,
        boldFont,
        italicFont,
        monoFont,
        notes
      );

      // renderMeasuredParagraph always appends +2 after the last line and +5 paragraph spacing
      // (total +7). Remove that extra so item spacing is controlled only by the +2.5 below.
      y = result.newY - 7;

      if (item.children && item.children.length > 0) {
        for (const childList of item.children) {
          const measuredChild = this.measureList(
            childList,
            lineWidth,
            font,
            boldFont,
            italicFont,
            monoFont,
            page,
            currentIndent
          );

          const childResult = this.renderMeasuredList(
            page,
            measuredChild,
            x,
            y,
            lineWidth,
            font,
            boldFont,
            italicFont,
            monoFont,
            notes,
            currentIndent
          );

          y = childResult.newY;
        }
      }

      y += 2.5;
    }

    return { newY: y };
  }

  private renderMeasuredBlockquote(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    font: PdfFont,
    italicFont: PdfFont
  ): { newY: number; footnote?: Note } {
    const element = measured.data as BlockquoteElement;

    const quotePen = new PdfPen(PdfColor.fromRgb(100, 100, 100), 2);
    const quoteStart = new PdfPoint(x, currentY);
    const quoteEnd = new PdfPoint(x, currentY + 15);
    page.drawLine(quotePen, quoteStart, quoteEnd);

    const rect = new PdfRect(x + 10, currentY, lineWidth - 20, 15);
    page.drawString(element.content, italicFont, PdfBrush.Black, null, rect, ETextAlign.CenterLeft);

    return { newY: currentY + 20 };
  }

  private renderMeasuredCodeBlock(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    monoFont: PdfFont
  ): { newY: number; footnote?: Note } {
    const element = measured.data as CodeBlockElement;

    const bgRect = new PdfRect(x, currentY, lineWidth, measured.height);
    page.drawRectangle(null, new PdfBrush(PdfColor.fromRgb(240, 240, 240)), bgRect);

    const borderPen = new PdfPen(PdfColor.fromRgb(200, 200, 200), 1);
    page.drawRectangle(borderPen, null, bgRect);

    const padding = 10;
    const lineHeight = monoFont.size * 1.5;
    let y = currentY + padding;

    const lines = element.content.split('\n');
    for (const line of lines) {
      const rect = new PdfRect(x + padding, y, lineWidth - padding * 2, lineHeight);
      page.drawString(line, monoFont, PdfBrush.Black, null, rect, ETextAlign.CenterLeft);
      y += lineHeight;
    }

    return { newY: currentY + measured.height + 10 };
  }

  private renderMeasuredTable(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont
  ): { newY: number; footnote?: Note } {
    const element = measured.data as TableElement & { colWidths?: number[]; rowHeights?: number[] };
    let y = currentY;

    const colCount = element.headers.length;

    // Use calculated column widths or fall back to equal distribution
    const colWidths = element.colWidths || Array(colCount).fill(lineWidth / colCount);
    const rowHeights = element.rowHeights || Array(element.rows.length).fill(18);

    // Calculate total table width
    const tableWidth = colWidths.reduce((sum, w) => sum + w, 0);

    // Render headers
    const headerBgRect = new PdfRect(x, y, tableWidth, 20);
    page.drawRectangle(null, new PdfBrush(PdfColor.fromRgb(240, 240, 240)), headerBgRect);

    let currentX = x;
    for (let i = 0; i < colCount; i++) {
      const cellRect = new PdfRect(currentX, y, colWidths[i], 20);

      // Render header inline content if available
      const headerInlines = element.headerCells?.[i];
      if (headerInlines && headerInlines.length > 0) {
        this.renderInlineContentInCell(page, headerInlines, cellRect, boldFont, ETextAlign.Center);
      } else {
        page.drawString(
          element.headers[i],
          boldFont,
          PdfBrush.Black,
          null,
          cellRect,
          ETextAlign.Center
        );
      }

      const borderPen = new PdfPen(PdfColor.fromRgb(100, 100, 100), 0.5);
      page.drawRectangle(borderPen, null, cellRect);

      currentX += colWidths[i];
    }

    y += 20;

    const separatorRect = new PdfRect(x, y, tableWidth, 2);
    page.drawRectangle(null, new PdfBrush(PdfColor.fromRgb(100, 100, 100)), separatorRect);
    y += 2;

    // Render data rows
    for (let rowIdx = 0; rowIdx < element.rows.length; rowIdx++) {
      currentX = x;
      const rowHeight = rowHeights[rowIdx] || 18;

      for (let i = 0; i < colCount; i++) {
        const cellRect = new PdfRect(currentX, y, colWidths[i], rowHeight);

        // Render cell inline content if available
        const cellInlines = element.cells?.[rowIdx]?.[i];
        if (cellInlines && cellInlines.length > 0) {
          this.renderInlineContentInCell(page, cellInlines, cellRect, font, ETextAlign.Center);
        } else {
          page.drawString(
            element.rows[rowIdx][i] || '',
            font,
            PdfBrush.Black,
            null,
            cellRect,
            ETextAlign.Center
          );
        }

        const borderPen = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
        page.drawRectangle(borderPen, null, cellRect);

        currentX += colWidths[i];
      }
      y += rowHeight;
    }

    return { newY: y + 10 };
  }

  private renderInlineContentInCell(
    page: PageGen,
    inlines: InlineElement[],
    rect: PdfRect,
    font: PdfFont,
    align: ETextAlign
  ): void {
    const lineHeight = font.size * 1.2;

    // Simple rendering - place all inline content centered in the cell
    let currentX = rect.left + 5;

    for (const inline of inlines) {
      switch (inline.type) {
        case 'text': {
          const textWidth = this.measureTextWidth(inline.content, font, page);
          const textRect = new PdfRect(
            currentX,
            rect.top + (rect.height - lineHeight) / 2,
            textWidth,
            lineHeight
          );
          page.drawString(
            inline.content,
            font,
            PdfBrush.Black,
            null,
            textRect,
            ETextAlign.CenterLeft
          );
          currentX += textWidth;
          break;
        }
        case 'bold':
        case 'italic': {
          // Render children with appropriate font
          for (const child of inline.children || []) {
            if (child.type === 'text') {
              const textWidth = this.measureTextWidth(child.content, font, page);
              const textRect = new PdfRect(
                currentX,
                rect.top + (rect.height - lineHeight) / 2,
                textWidth,
                lineHeight
              );
              page.drawString(
                child.content,
                font,
                PdfBrush.Black,
                null,
                textRect,
                ETextAlign.CenterLeft
              );
              currentX += textWidth;
            }
          }
          break;
        }
        case 'code': {
          const textWidth = this.measureTextWidth(inline.content, font, page);
          const textRect = new PdfRect(
            currentX,
            rect.top + (rect.height - lineHeight) / 2,
            textWidth,
            lineHeight
          );
          page.drawString(
            inline.content,
            font,
            PdfBrush.Black,
            null,
            textRect,
            ETextAlign.CenterLeft
          );
          currentX += textWidth;
          break;
        }
        case 'latex': {
          const latexWidth = page.measureLatex(inline.content, font.size).width;
          const latexRect = new PdfRect(
            currentX,
            rect.top + (rect.height - lineHeight) / 2,
            latexWidth,
            lineHeight
          );
          page.drawLatex(
            inline.content,
            latexRect,
            ETextAlign.CenterLeft,
            font.size,
            PdfBrush.Black,
            false
          );
          currentX += latexWidth;
          break;
        }
        case 'module': {
          const mod = moduleRegistry.get(inline.moduleName);
          if (mod) {
            const content = mod.parse(inline.content, inline.attrs);
            const size = mod.measure(content, { font, page });
            const modRect = new PdfRect(
              currentX,
              rect.top + (rect.height - lineHeight) / 2,
              size.width,
              lineHeight
            );
            mod.draw(content, modRect, ETextAlign.CenterLeft, {
              font,
              brush: PdfBrush.Black,
              page,
            });
            currentX += size.width;
          }
          break;
        }
      }
      // Add small space between inline elements
      currentX += this.measureTextWidth(' ', font, page);
    }
  }

  private renderMeasuredModule(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    font: PdfFont
  ): { newY: number; footnote?: Note } {
    const data = measured.data as { element: ModuleElement; content: any; module: any };

    if (data.module) {
      try {
        const rect = new PdfRect(x, currentY, measured.width, measured.height);
        data.module.draw(data.content, rect, ETextAlign.CenterLeft, { font, page });
        return { newY: currentY + measured.height + 10 };
      } catch (e) {
        const errorRect = new PdfRect(x, currentY, lineWidth, 15);
        page.drawString(
          `Error: ${(e as Error).message}`,
          font,
          PdfBrush.Red,
          null,
          errorRect,
          ETextAlign.CenterLeft
        );
        return { newY: currentY + 20 };
      }
    }

    return { newY: currentY + 20 };
  }

  private renderMeasuredFloat(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number,
    font: PdfFont,
    boldFont: PdfFont,
    italicFont: PdfFont,
    monoFont: PdfFont,
    notes: NotesCollection
  ): { newY: number; footnote?: Note } {
    const data = measured.data as { element: FloatElement; measuredContents: MeasuredElement[] };

    // Render each float content block sequentially, x is already the correct position.
    let floatY = currentY;
    for (const measuredContent of data.measuredContents) {
      const result = this.renderMeasuredElement(
        page,
        measuredContent,
        x,
        floatY,
        measured.width,
        font,
        boldFont,
        italicFont,
        monoFont,
        notes
      );
      floatY = result.newY;
    }

    // Do NOT advance currentY — text after this float should flow beside it.
    // floatBottomY in the main render loop tracks when to clear the float reservation.
    return { newY: currentY };
  }

  private renderMeasuredHr(
    page: PageGen,
    measured: MeasuredElement,
    x: number,
    currentY: number,
    lineWidth: number
  ): { newY: number; footnote?: Note } {
    const pen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.5);
    const start = new PdfPoint(x, currentY + 5);
    const end = new PdfPoint(x + lineWidth, currentY + 5);
    page.drawLine(pen, start, end);

    return { newY: currentY + 15 };
  }

  /**
   * Computes the footnote zone height for N notes, matching renderFootnotes' positioning.
   * separatorY = pageHeight - margin - N*13 - 5, so reserve = margin + N*13 + 5.
   */
  private calcFootnoteReserve(noteCount: number): number {
    if (noteCount === 0) return this.options.margin + 5; // minimal: just margin + small buffer
    return this.options.margin + noteCount * 13 + 5;
  }

  private renderFootnotes(
    page: PageGen,
    footnotes: Note[],
    x: number,
    width: number,
    font: PdfFont
  ): void {
    if (footnotes.length === 0) return;

    const pageHeight = PdfSize.A4.height;
    const noteLineHeight = 13;
    // Position separator so notes fit between it and the bottom margin
    const totalNotesHeight = footnotes.length * noteLineHeight;
    const separatorY = pageHeight - this.options.margin - totalNotesHeight - 5;

    const linePen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.5);
    page.drawLine(linePen, new PdfPoint(x, separatorY), new PdfPoint(x + width * 0.4, separatorY));

    let y = separatorY + 3;
    const noteFont = new PdfFont(PdfFont.FONT_NAME, 8.5);
    for (const note of footnotes) {
      const marker =
        note.type === 'citation' ? `[${note.number}]` : renderFootnoteMarker(note.number || 0);
      const text = `${marker} ${note.content}`;
      const rect = new PdfRect(x + 5, y, width - 10, noteLineHeight);
      page.drawString(text, noteFont, PdfBrush.Black, null, rect, ETextAlign.CenterLeft);
      y += noteLineHeight;
    }
  }
}

export function renderBook(
  pdf: PdfGen,
  book: Book,
  notes: NotesCollection,
  options?: BookOptions
): RenderResult {
  const renderer = new BookRenderer(options);
  return renderer.render(pdf, book, notes);
}
